// Custom domain. The API moves a domain through pending_dns, verified,
// provisioning and active; `failed` means activation threw.
import { useState } from 'react'
import {
  AlertTriangle, Check, CheckCircle2, Clock, Copy, ExternalLink, Loader2, RefreshCw, ShieldCheck, Trash2,
} from 'lucide-react'
import { adminApi } from '../../services/api'
import { READ_ONLY_MESSAGE } from '../../utils/readerAccess'
import { ConfirmDialog, Panel } from './SettingsPrimitives'
import { DOMAIN_STATUS, STATUS_TONES } from './useCustomDomain'

const STEPS = ['Domain', 'DNS', 'Certificate', 'Live']
const CURRENT_STEP = { pending_dns: 1, verified: 2, provisioning: 2, failed: 2, active: 4 }

function Stepper({ status }) {
  const current = status ? CURRENT_STEP[status] ?? 1 : 0
  return (
    <ol className="grid grid-cols-4 gap-2" aria-label="Setup progress">
      {STEPS.map((label, i) => {
        const done = i < current
        const now = Boolean(status) && i === current
        const failed = now && status === 'failed'
        const working = now && status === 'provisioning'
        return (
          <li key={label} className="min-w-0" aria-current={now ? 'step' : undefined}>
            <span className="block h-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <span
                className={`block h-full origin-left rounded-full transition-[transform,background-color] duration-500 ease-out motion-reduce:transition-none
                            ${done ? 'bg-emerald-500' : failed ? 'bg-red-500' : 'bg-navy-500'}`}
                style={{ transform: `scaleX(${done ? 1 : now ? 0.45 : 0})`, transitionDelay: `${i * 90}ms` }}
              />
            </span>
            <span className={`mt-2 flex items-center gap-1 truncate text-xs font-medium transition-colors duration-300
                              ${done ? 'text-emerald-700 dark:text-emerald-400'
                                : failed ? 'text-red-600 dark:text-red-400'
                                  : now ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}`}>
              {done && <Check className="h-3 w-3 shrink-0" strokeWidth={3} />}
              {working && <Loader2 className="h-3 w-3 shrink-0 animate-spin" />}
              {failed && <AlertTriangle className="h-3 w-3 shrink-0" />}
              {label}
            </span>
          </li>
        )
      })}
    </ol>
  )
}

// Click to copy. The icon swaps to a tick for a moment.
function CopyValue({ label, value, toast }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      toast.error("Couldn't copy", 'Select the value and copy it manually.')
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={`Copy ${label.toLowerCase()}`}
      className="group flex w-full min-w-0 items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-left transition-colors
                 hover:bg-gray-100 dark:hover:bg-gray-800"
    >
      <span className="min-w-0">
        <span className="block text-[11px] text-gray-500 dark:text-gray-400">{label}</span>
        <span className="block break-all font-mono text-xs text-gray-900 dark:text-gray-100">{value}</span>
      </span>
      <span className="relative h-4 w-4 shrink-0">
        <Copy className={`absolute inset-0 h-4 w-4 text-gray-300 transition-[opacity,transform,color] duration-200 group-hover:text-gray-500
                          dark:text-gray-600 dark:group-hover:text-gray-300 ${copied ? 'scale-50 opacity-0' : 'scale-100 opacity-100'}`} />
        <Check className={`absolute inset-0 h-4 w-4 text-emerald-500 transition-[opacity,transform] duration-200
                           ${copied ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`} strokeWidth={3} />
      </span>
    </button>
  )
}

function StatusPill({ status }) {
  const meta = DOMAIN_STATUS[status] || DOMAIN_STATUS.pending_dns
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${STATUS_TONES[meta.tone]}`}>
      {meta.label}
    </span>
  )
}

export default function DomainSection({ domain: hook, hostname, setHostname, appName, isReader, toast, bindSpot }) {
  const { state, loading, loadError, action, load, run } = hook
  const [confirmRemove, setConfirmRemove] = useState(false)

  if (loading && !state) {
    return (
      <Panel>
        <div className="space-y-3">
          <div className="skeleton h-5 w-48" />
          <div className="skeleton h-4 w-72 max-w-full" />
          <div className="skeleton mt-4 h-10 w-full" />
        </div>
      </Panel>
    )
  }

  if (loadError && !state) {
    return (
      <Panel title="Custom domain">
        <p className="flex items-center gap-2 text-[13px] text-red-600 dark:text-red-400">
          <AlertTriangle className="h-4 w-4 shrink-0" /> Couldn&apos;t load the domain. {loadError}
        </p>
        <button type="button" onClick={load} className="btn btn-secondary btn-sm mt-4 gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Try again
        </button>
      </Panel>
    )
  }

  if (!(state?.configured && state?.domain)) {
    const submit = async e => {
      e.preventDefault()
      await run('configure', () => adminApi.setCustomDomain(hostname.trim()))
    }
    return (
      <Panel title="Custom domain" description={`Serve ${appName || 'Xtract'} from your own subdomain.`}>
        <form onSubmit={submit} className="flex flex-col gap-2 sm:flex-row">
          <div {...bindSpot('host')} className="flex min-w-0 flex-1">
            <span className="inline-flex select-none items-center rounded-l-lg border border-r-0 border-gray-200 bg-gray-50 px-3 text-sm text-gray-500
                             dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
              https://
            </span>
            <input
              id="settings-domain"
              type="text"
              aria-label="Subdomain"
              className="form-input !rounded-l-none"
              value={hostname}
              onChange={e => setHostname(e.target.value)}
              placeholder="app.yourcompany.com"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              disabled={isReader || action === 'configure'}
            />
          </div>
          <button
            type="submit"
            disabled={isReader || !hostname.trim() || action === 'configure'}
            title={isReader ? READ_ONLY_MESSAGE : undefined}
            className="btn btn-primary shrink-0 gap-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {action === 'configure' && <Loader2 className="h-4 w-4 animate-spin" />}
            Add domain
          </button>
        </form>
        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Root domains like yourcompany.com aren&apos;t supported.</p>
        <div className="mt-6">
          <Stepper status={null} />
        </div>
      </Panel>
    )
  }

  const domain = state.domain
  const active = domain.status === 'active'
  const provisioning = domain.status === 'provisioning'
  const canActivate = ['verified', 'provisioning', 'failed'].includes(domain.status)
  const busy = Boolean(action)

  const remove = async () => {
    const ok = await run('remove', () => adminApi.removeCustomDomain())
    setConfirmRemove(false)
    if (ok) setHostname('')
  }

  return (
    <>
      <Panel>
        <div {...bindSpot('host')} className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <h2 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">{domain.hostname}</h2>
            <StatusPill status={domain.status} />
          </div>
          {active && (
            <a href={`https://${domain.hostname}`} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm gap-1.5">
              Open <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>

        <div className="mt-5">
          <Stepper status={domain.status} />
        </div>

        {active ? (
          <p className="mt-6 flex items-center gap-2 text-[13px] text-emerald-700 animate-panel-in dark:text-emerald-400">
            <CheckCircle2 className="h-4 w-4 shrink-0" /> Live with HTTPS. Sign-in and CORS are set up.
          </p>
        ) : (
          <div className="mt-6">
            <p className="text-[13px] font-medium text-gray-900 dark:text-gray-100">Add these records at your DNS provider</p>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Point the CNAME straight at the target. Don&apos;t proxy it.</p>
            <div className="mt-3 divide-y divide-gray-100 rounded-xl border border-gray-200 dark:divide-gray-800 dark:border-gray-700">
              {[
                { type: 'CNAME', host: domain.dns.cname_host, value: domain.dns.cname_target },
                { type: 'TXT', host: domain.dns.txt_host, value: domain.dns.txt_value },
              ].map(record => (
                <div key={record.type} className="flex items-start gap-2 p-1.5">
                  <span className="mt-2.5 w-14 shrink-0 rounded-md bg-gray-900 py-0.5 text-center text-[10.5px] font-bold tracking-wide text-white dark:bg-gray-700">
                    {record.type}
                  </span>
                  <div className="min-w-0 flex-1">
                    <CopyValue label="Name" value={record.host} toast={toast} />
                    <CopyValue label="Value" value={record.value} toast={toast} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {provisioning && !domain.last_error && (
          <p className="mt-4 flex items-center gap-2 text-[13px] text-gray-600 animate-panel-in dark:text-gray-300">
            <Clock className="h-4 w-4 shrink-0 text-violet-500" /> Azure is issuing the certificate. Check again in a few minutes.
          </p>
        )}

        {domain.last_error && !active && (
          <p className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2.5 text-[13px] text-red-700 animate-panel-in dark:bg-red-900/20 dark:text-red-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="break-words">{domain.last_error}</span>
          </p>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4 dark:border-gray-800">
          {!active && (
            <button
              type="button"
              disabled={isReader || busy}
              onClick={() => run('verify', adminApi.verifyCustomDomain)}
              className={`btn btn-sm gap-1.5 disabled:opacity-50 ${canActivate ? 'btn-secondary' : 'btn-primary'}`}
            >
              {action === 'verify' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Verify DNS
            </button>
          )}
          {canActivate && (
            <button
              type="button"
              disabled={isReader || busy}
              onClick={() => run('activate', adminApi.activateCustomDomain)}
              className="btn btn-primary btn-sm gap-1.5 disabled:opacity-50"
            >
              {action === 'activate' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              {provisioning ? 'Check again' : 'Activate'}
            </button>
          )}
          <button
            type="button"
            disabled={isReader || busy}
            onClick={() => setConfirmRemove(true)}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-medium text-red-600 transition-colors
                       hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />
            Remove
          </button>
        </div>
      </Panel>

      <ConfirmDialog
        open={confirmRemove}
        title="Remove this domain?"
        body={(
          <>
            Xtract will unbind <span className="break-all font-medium text-gray-700 dark:text-gray-200">{domain.hostname}</span> and
            remove its certificate and sign-in redirect. You can delete the DNS records afterwards.
          </>
        )}
        confirmLabel="Remove"
        busy={action === 'remove'}
        onConfirm={remove}
        onCancel={() => setConfirmRemove(false)}
      />
    </>
  )
}
